import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import Request from "../../core/httpClient";

const request = new Request();

// ─── Column Aliases ──────────────────────────────────────────────────────────
const UNIDAD_ALIASES = ["UNIDAD", "VIVIENDA", "VIVIENDAS"];
const FECHA_ALIASES = ["FECHA", "DATE", "FECHA_CARGO", "FECHA_MM_AAAA", "FECHA (MM/AAAA)"];
const CODIGO_ALIASES = ["CODIGO", "CÓDIGO", "CODIGO_PRODUCTO", "CÓDIGO_PRODUCTO", "CODIGO PRODUCTO", "CÓDIGO PRODUCTO"];
const COMENTARIOS_ALIASES = ["COMENTARIOS", "COMENTARIO", "COMMENT", "OBSERVACIONES"];
const IMPORTE_ALIASES = ["IMPORTE", "MONTO", "AMOUNT", "TOTAL"];

const COL_UNIDAD = "UNIDAD";
const COL_FECHA = "FECHA";
const COL_CODIGO = "CODIGO";
const COL_COMENTARIOS = "COMENTARIOS";
const COL_IMPORTE = "IMPORTE";

const REQUIRED_COLUMN_DEFS = [
  { key: COL_UNIDAD, label: "Vivienda", aliases: UNIDAD_ALIASES, required: true },
  { key: COL_FECHA, label: "Fecha (MM/AAAA)", aliases: FECHA_ALIASES, required: true },
  { key: COL_CODIGO, label: "Código", aliases: CODIGO_ALIASES, required: true },
  { key: COL_COMENTARIOS, label: "Comentarios", aliases: COMENTARIOS_ALIASES, required: false },
  { key: COL_IMPORTE, label: "Importe", aliases: IMPORTE_ALIASES, required: true },
];

const TEMPLATE_HEADERS = ["VIVIENDA", "FECHA (MM/AAAA)", "CÓDIGO", "COMENTARIOS", "IMPORTE"];

const TEMPLATE_PATH = "/plantillas/masivo_documentos_cobro.xlsx";

// ─── Error code → column mapping ─────────────────────────────────────────────
const ERROR_CODE_COLUMN_MAP = {
  HOUSE_NOT_FOUND: COL_UNIDAD,
  PRODUCT_NOT_FOUND: COL_CODIGO,
  DATE_NOT_ALLOWED_FOR_PRODUCT: COL_FECHA,
  DUPLICATE_FOUND: [COL_UNIDAD, COL_FECHA, COL_CODIGO],
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const normalize = s =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ");

const upperKey = s =>
  normalize(s)
    .toUpperCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_|_$/g, "");

const pad2 = value => String(value).padStart(2, "0");

const parseDateCell = value => {
  const result = (month, year) => ({
    month,
    year,
    display: `${pad2(month)}/${year}`,
  });

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return result(value.getMonth() + 1, value.getFullYear());
  }

  if (typeof value === "number" && Number.isFinite(value) && XLSX?.SSF?.parse_date_code) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) return result(parsed.m, parsed.y);
  }

  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const normalized = raw.replace(/\./g, "/").replace(/-/g, "/");
  const parts = normalized
    .split("/")
    .map(p => p.trim())
    .filter(Boolean);

  // MM/YYYY
  if (parts.length === 2) {
    const month = Number(parts[0]);
    let year = Number(parts[1]);
    if (year < 100) year += year >= 70 ? 1900 : 2000;
    if (Number.isFinite(month) && Number.isFinite(year) && month >= 1 && month <= 12) {
      return result(month, year);
    }
  }

  // DD/MM/YYYY — extract month and year only
  if (parts.length === 3) {
    let year, month;
    if (parts[0].length === 4) {
      year = Number(parts[0]);
      month = Number(parts[1]);
    } else if (parts[2].length >= 2) {
      month = Number(parts[1]);
      year = Number(parts[2]);
      if (year < 100) year += year >= 70 ? 1900 : 2000;
    }
    if (Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12) {
      return result(month, year);
    }
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return result(parsed.getMonth() + 1, parsed.getFullYear());
  }

  return null;
};

const formatCurrency = value => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(num);
};

// ─── Component ───────────────────────────────────────────────────────────────
const Importar = React.forwardRef(function Importar(props, ref) {
  const [excelName, setExcelName] = useState("");
  const [columns, setColumns] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [serverRows, setServerRows] = useState([]);
  const [validating, setValidating] = useState(false);
  const [formatErrors, setFormatErrors] = useState([]);

  const fileInputRef = useRef(null);

  const idFrac = useMemo(
    () => props.id_fraccionamiento ?? +(localStorage.getItem("frac") || 0),
    [props.id_fraccionamiento],
  );

  // ── Column mapping ────────────────────────────────────────────────────────
  const columnMap = useMemo(() => {
    const map = {};
    for (const def of REQUIRED_COLUMN_DEFS) {
      const aliasKeys = def.aliases.map(a => upperKey(a));
      const col = columns.find(c => aliasKeys.includes(upperKey(c.key)));
      map[def.key] = col ? col.key : null;
    }
    return map;
  }, [columns]);

  // Build column map from a given columns array (used before state updates)
  const buildColumnMap = useCallback(cols => {
    const map = {};
    for (const def of REQUIRED_COLUMN_DEFS) {
      const aliasKeys = def.aliases.map(a => upperKey(a));
      const col = cols.find(c => aliasKeys.includes(upperKey(c.key)));
      map[def.key] = col ? col.key : null;
    }
    return map;
  }, []);

  // ── Validate with server (calls SP) ───────────────────────────────────────
  const validateWithServer = useCallback(
    async (rows, colMap) => {
      const unidadKey = colMap[COL_UNIDAD];
      const fechaKey = colMap[COL_FECHA];
      const codigoKey = colMap[COL_CODIGO];
      const importeKey = colMap[COL_IMPORTE];

      const clientErrors = [];

      const comentariosKey = colMap[COL_COMENTARIOS];

      const lineas = rows.map((row, idx) => {
        const unidad = String(row?.[unidadKey] ?? "").trim();
        const fechaRaw = row?.[fechaKey];
        const fechaParsed = parseDateCell(fechaRaw);
        const fecha = fechaParsed ? `${pad2(fechaParsed.month)}/${fechaParsed.year}` : "";
        const codigo = String(row?.[codigoKey] ?? "").trim();
        const importeRaw = row?.[importeKey];
        const importe = parseFloat(importeRaw);
        const comentario = comentariosKey ? String(row?.[comentariosKey] ?? "").trim() : "";
        const rowNum = idx + 1;

        // Basic format validation (client-side)
        if (!unidad)
          clientErrors.push({ rowIndex: idx, key: unidadKey, message: "La vivienda es obligatoria." });
        if (!fecha)
          clientErrors.push({ rowIndex: idx, key: fechaKey, message: "Fecha inválida o vacía (MM/AAAA)." });
        if (!codigo)
          clientErrors.push({ rowIndex: idx, key: codigoKey, message: "El código es obligatorio." });
        if (Number.isNaN(importe))
          clientErrors.push({ rowIndex: idx, key: importeKey, message: "El importe debe ser un número válido." });
        else if (importe <= 0)
          clientErrors.push({ rowIndex: idx, key: importeKey, message: "El importe debe ser mayor a 0." });
        if (comentario.length > 200)
          clientErrors.push({ rowIndex: idx, key: comentariosKey, message: `El comentario excede 200 caracteres (${comentario.length}).` });

        return {
          rowNumber: rowNum,
          vivienda: unidad,
          fecha,
          codigo_producto: codigo,
          importe: Number.isNaN(importe) ? 0 : importe,
        };
      });

      setFormatErrors(clientErrors);
      setValidating(true);

      try {
        const data = {
          id_fraccionamiento: idFrac,
          doc_lineas: JSON.stringify(lineas),
        };

        const response = await request.post("/documentos/validate/import", data);
        const { tabla, error, message } = response || { error: true };

        // console.log("Validation response:", response);

        if (error || !Array.isArray(tabla)) {
          console.error("Validation error:", message || error);
          setServerRows([]);
          return;
        }

        setServerRows(tabla);
      } catch (err) {
        console.error("Validation fetch error:", err);
        setServerRows([]);
      } finally {
        setValidating(false);
      }
    },
    [idFrac],
  );

  // ── Read Excel file ───────────────────────────────────────────────────────
  const handleFileChange = async event => {
    const files = event.target.files;
    if (!files || !files[0]) {
      setExcelName("");
      setColumns([]);
      setTableData([]);
      setServerRows([]);
      setFormatErrors([]);
      return;
    }

    const file = files[0];
    setExcelName(file.name);

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array", cellDates: true, cellNF: false, cellText: false });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) {
        setColumns([]);
        setTableData([]);
        setServerRows([]);
        return;
      }

      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: false });
      if (!rows || rows.length < 2) {
        setColumns([]);
        setTableData([]);
        setServerRows([]);
        return;
      }

      const headerRow = rows[0];
      const cols = headerRow
        .map((cell, idx) => {
          const label = String(cell ?? "").trim();
          if (!label) return null;
          return { key: upperKey(label), label, sourceIndex: idx };
        })
        .filter(Boolean);

      const seen = new Set();
      const uniqueCols = [];
      for (const c of cols) {
        if (!seen.has(c.key)) {
          seen.add(c.key);
          uniqueCols.push(c);
        }
      }

      const dataRows = rows.slice(1);
      const parsed = dataRows.map(r => {
        const obj = {};
        uniqueCols.forEach(c => {
          obj[c.key] = r[c.sourceIndex] ?? "";
        });
        return obj;
      });

      setColumns(uniqueCols);
      setTableData(parsed);
      setServerRows([]);

      // Check required columns and fire server validation
      const colMap = buildColumnMap(uniqueCols);
      const missingCols = REQUIRED_COLUMN_DEFS.filter(d => d.required && !colMap[d.key]);

      if (missingCols.length === 0 && parsed.length > 0) {
        await validateWithServer(parsed, colMap);
      }
    } catch (err) {
      console.error("XLSX parse error:", err);
      setColumns([]);
      setTableData([]);
      setServerRows([]);
    }

    if (event?.target) event.target.value = "";
  };

  // ── Download template ─────────────────────────────────────────────────────
  const handleDownloadTemplate = useCallback(() => {
    try {
      const link = document.createElement("a");
      link.href = TEMPLATE_PATH;
      link.download = "masivo_documentos_cobro.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      window.open(TEMPLATE_PATH, "_blank", "noopener,noreferrer");
    }
  }, []);

  // ── Reset when file name is cleared ───────────────────────────────────────
  useEffect(() => {
    if (!excelName && fileInputRef.current) fileInputRef.current.value = "";
  }, [excelName]);

  // ── Server row map (id_linea → row) ───────────────────────────────────────
  const serverRowMap = useMemo(() => {
    const map = new Map();
    for (const sr of serverRows) {
      map.set(sr.id_linea, sr);
    }
    return map;
  }, [serverRows]);

  // ── Validation summary (uses server results) ─────────────────────────────
  const validationSummary = useMemo(() => {
    const errors = [];
    const warnings = [];
    const cellIssues = tableData.map(() => ({}));

    const registerCellIssue = (rowIndex, columnKey, message, type = "error") => {
      if (rowIndex == null || rowIndex < 0 || !columnKey || !cellIssues[rowIndex]) return;
      if (!cellIssues[rowIndex][columnKey]) {
        cellIssues[rowIndex][columnKey] = { type, messages: [] };
      }
      cellIssues[rowIndex][columnKey].messages.push(message);
    };

    const emptyResult = {
      errors,
      warnings,
      rows: [],
      cellIssues,
      subtotal: 0,
      impuestos: 0,
      retenciones: 0,
      total: 0,
    };

    if (!excelName || tableData.length === 0) {
      return emptyResult;
    }

    // Check required columns exist
    const missingCols = [];
    for (const def of REQUIRED_COLUMN_DEFS) {
      if (!def.required) continue;
      if (!columnMap[def.key]) missingCols.push(def.label);
    }
    if (missingCols.length > 0) {
      errors.push(`No se encontraron las columnas: ${missingCols.join(", ")}.`);
      return emptyResult;
    }

    const unidadKey = columnMap[COL_UNIDAD];
    const fechaKey = columnMap[COL_FECHA];
    const codigoKey = columnMap[COL_CODIGO];

    // Client-side format errors
    for (const fe of formatErrors) {
      const rowLabel = `Fila ${fe.rowIndex + 1}`;
      errors.push(`${rowLabel}: ${fe.message}`);
      registerCellIssue(fe.rowIndex, fe.key, fe.message, "error");
    }

    // Build duplicate groups (duplicado value → list of id_linea)
    const duplicateGroups = new Map();
    for (const sRow of serverRows) {
      if (sRow.duplicado != null) {
        if (!duplicateGroups.has(sRow.duplicado)) {
          duplicateGroups.set(sRow.duplicado, []);
        }
        duplicateGroups.get(sRow.duplicado).push(sRow.id_linea);
      }
    }

    // Server-side errors
    for (const sRow of serverRows) {
      const rowIdx = sRow.id_linea - 1;
      if (rowIdx < 0 || rowIdx >= tableData.length) continue;

      if (sRow.errores) {
        let rowErrors;
        try {
          rowErrors = typeof sRow.errores === "string" ? JSON.parse(sRow.errores) : sRow.errores;
        } catch {
          rowErrors = [];
        }

        if (Array.isArray(rowErrors)) {
          rowErrors = rowErrors.flat(Infinity);
          for (const err of rowErrors) {
            const unidad = sRow.num_registro || "";
            const rowLabel = unidad ? `Fila ${sRow.id_linea} (${unidad})` : `Fila ${sRow.id_linea}`;

            // Enhance message for duplicates to reference the other row(s)
            let message = err.message;
            if (err.code === "DUPLICATE_FOUND" && sRow.duplicado != null) {
              const group = duplicateGroups.get(sRow.duplicado) || [];
              const otherRows = group.filter(id => id !== sRow.id_linea);
              if (otherRows.length > 0) {
                message = `${err.message} (Duplicado con Fila${otherRows.length > 1 ? "s" : ""} ${otherRows.join(", ")})`;
              }
            }

            errors.push(`${rowLabel}: ${message}`);

            // Map error code to column key(s) — supports single key or array
            const colMapping = ERROR_CODE_COLUMN_MAP[err.code];
            const colKeys = Array.isArray(colMapping) ? colMapping : colMapping ? [colMapping] : [];
            for (const colDefKey of colKeys) {
              const mappedKey = columnMap[colDefKey];
              if (mappedKey) {
                registerCellIssue(rowIdx, mappedKey, message, "error");
              }
            }
          }
        }
      }
    }

    // Compute totals from server rows
    let sumSubtotal = 0;
    let sumImpuestos = 0;
    let sumRetenciones = 0;

    for (const sRow of serverRows) {
      sumSubtotal += sRow.importe || 0;
      sumImpuestos += sRow.impuestos || 0;
      sumRetenciones += sRow.retenciones || 0;
    }

    const totalGeneral = sumSubtotal + sumImpuestos - sumRetenciones;

    // Build valid rows (no server errors + no format errors)
    const formatErrorRows = new Set(formatErrors.map(fe => fe.rowIndex));
    const comentariosKey = columnMap[COL_COMENTARIOS];

    const validRows = serverRows
      .filter(sRow => !sRow.errores && !formatErrorRows.has(sRow.id_linea - 1))
      .map(sRow => {
        const rowIdx = sRow.id_linea - 1;
        const fechaParsed = parseDateCell(sRow.fecha);
        const comentarios =
          comentariosKey && tableData[rowIdx] ? String(tableData[rowIdx][comentariosKey] ?? "").trim() : "";

        return {
          rowNumber: sRow.id_linea,
          VIVIENDA: sRow.num_registro,
          id_vivienda: sRow.id_vivenda,
          MES: fechaParsed ? fechaParsed.month : null,
          AÑO: fechaParsed ? fechaParsed.year : null,
          CODIGO: sRow.codigo_producto,
          id_producto: sRow.id_producto,
          IMPORTE: sRow.importe,
          COMENTARIOS: comentarios,
          IMPUESTOS: sRow.impuestos || 0,
          RETENCIONES: sRow.retenciones || 0,
        };
      });

    errors.sort((a, b) => {
      const rowA = parseInt(a.match(/^Fila\s+(\d+)/)?.[1], 10) || 0;
      const rowB = parseInt(b.match(/^Fila\s+(\d+)/)?.[1], 10) || 0;
      return rowA - rowB;
    });

    return {
      errors,
      warnings,
      rows: validRows,
      cellIssues,
      subtotal: sumSubtotal,
      impuestos: sumImpuestos,
      retenciones: sumRetenciones,
      total: totalGeneral,
    };
  }, [columnMap, excelName, formatErrors, serverRows, tableData]);

  // ── Reset form state ──────────────────────────────────────────────────────
  const resetFormState = useCallback(() => {
    setExcelName("");
    setColumns([]);
    setTableData([]);
    setServerRows([]);
    setFormatErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  // ── Expose API via ref ────────────────────────────────────────────────────
  useImperativeHandle(
    ref,
    () => ({
      collectBulkPayload: () => {
        if (!excelName || tableData.length === 0) {
          return {
            errors: ["Debes importar un archivo con datos."],
            warnings: [],
            rows: [],
            cellIssues: [],
            subtotal: 0,
            impuestos: 0,
            retenciones: 0,
            total: 0,
          };
        }
        return validationSummary;
      },
      resetForm: resetFormState,
    }),
    [excelName, tableData, validationSummary, resetFormState],
  );

  // ── Totals (from validation) ──────────────────────────────────────────────
  const { subtotal, impuestos: impuestosTotal, retenciones: retencionesTotal, total } = validationSummary;

  // ── Display helpers ───────────────────────────────────────────────────────
  const ISSUES_PREVIEW_LIMIT = 5;
  const errorPreview = validationSummary.errors.slice(0, ISSUES_PREVIEW_LIMIT);
  const warningPreview = validationSummary.warnings.slice(0, ISSUES_PREVIEW_LIMIT);
  const extraErrors = validationSummary.errors.length - errorPreview.length;
  const extraWarnings = validationSummary.warnings.length - warningPreview.length;

  const displayColumns = useMemo(() => {
    if (columns.length > 0) return columns;
    return REQUIRED_COLUMN_DEFS.map(d => ({ key: d.key, label: d.label }));
  }, [columns]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="container">
      <div className="column full">
        <div className="white-space-24" />

        {/* File input + Template */}
        <div className="input-form-content row full align-center">
          <div className="column label-big">
            <p>
              <b>Buscar archivo (xlsx):</b>
            </p>
          </div>
          <div className="column full">
            <div className="row align-center">
              <div className="column full">
                <input
                  type="file"
                  className="input input-modals input-file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                />
                <input
                  type="text"
                  disabled
                  className="input input-modals"
                  value={excelName || ""}
                  placeholder="Selecciona un archivo"
                />
              </div>
            </div>
          </div>
          <div className="column">
            <button
              type="button"
              className="btn btn-primary btn-small color-white label-input-file"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                  fileInputRef.current.click();
                }
              }}
            >
              Importar
            </button>
          </div>
          &nbsp;
          <div className="column">
            <button type="button" className="btn btn-primary btn-small color-white" onClick={handleDownloadTemplate}>
              Plantilla
            </button>
          </div>
        </div>

        <div className="white-space-16" />

        {/* Validating indicator */}
        {validating && (
          <div style={{ textAlign: "center", padding: "12px 0", color: "#666" }}>
            <i className="fas fa-spinner fa-spin" /> &nbsp; Validando datos con el servidor...
          </div>
        )}

        {/* Table */}
        <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden", minWidth: 0 }}>
          <div style={{ maxHeight: 360, overflowX: "auto", overflowY: "auto", width: "100%", minWidth: 0 }}>
            <table className="table full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr>
                  {displayColumns.map(col => {
                    const def = REQUIRED_COLUMN_DEFS.find(d =>
                      d.aliases.map(a => upperKey(a)).includes(upperKey(col.key)),
                    );
                    const label = def ? def.label : col.label;
                    const isImporte = def?.key === COL_IMPORTE;
                    return (
                      <th key={col.key} className={isImporte ? "text-center" : "text-left"}>
                        {label}
                      </th>
                    );
                  })}
                  <th className="text-center">Estatus</th>
                </tr>
              </thead>

              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan={displayColumns.length + 1} className="text-center">
                      No hay datos que mostrar
                    </td>
                  </tr>
                ) : (
                  tableData.map((row, rIdx) => {
                    const sRow = serverRowMap.get(rIdx + 1);
                    const hasServerErrors = sRow?.errores != null;
                    const hasFormatErrors = formatErrors.some(fe => fe.rowIndex === rIdx);
                    const rowHasErrors = hasServerErrors || hasFormatErrors;
                    const rowValidated = sRow != null;

                    return (
                      <tr key={rIdx}>
                        {displayColumns.map(col => {
                          const def = REQUIRED_COLUMN_DEFS.find(d =>
                            d.aliases.map(a => upperKey(a)).includes(upperKey(col.key)),
                          );
                          const isImporte = def?.key === COL_IMPORTE;
                          const isFecha = def?.key === COL_FECHA;

                          const issue = validationSummary.cellIssues?.[rIdx]?.[col.key];
                          const hasIssue = issue && Array.isArray(issue.messages) && issue.messages.length > 0;
                          const issueBorderColor = issue?.type === "warning" ? "#e0a500" : "#d14343";
                          const issueBgColor = issue?.type === "warning" ? "#fff8e5" : "#fdecea";

                          const cellStyle = { padding: "8px 6px", wordWrap: "break-word" };
                          if (hasIssue) {
                            cellStyle.background = issueBgColor;
                            cellStyle.boxShadow = `inset 0 0 0 2px ${issueBorderColor}`;
                            cellStyle.cursor = "help";
                          }

                          let displayValue = row[col.key];
                          if (isImporte) {
                            displayValue = formatCurrency(displayValue);
                          } else if (isFecha) {
                            const p = parseDateCell(displayValue);
                            displayValue = p ? p.display : String(displayValue ?? "");
                          } else {
                            displayValue = String(displayValue ?? "");
                          }

                          const title = hasIssue ? issue.messages.join(" · ") : displayValue;

                          return (
                            <td
                              key={col.key}
                              className={isImporte ? "text-right" : "text-left"}
                              style={cellStyle}
                              title={title}
                            >
                              {displayValue}
                            </td>
                          );
                        })}
                        <td className="text-center" style={{ width: "14%" }}>
                          {validating ? (
                            <i className="fas fa-spinner fa-spin color-gray" />
                          ) : rowValidated ? (
                            <div
                              className="tooltip right"
                              data-tooltip={
                                rowHasErrors ? "Tiene errores de validación" : "Validación correcta"
                              }
                            >
                              {rowHasErrors ? (
                                <i className="fas fa-exclamation-circle color-red" />
                              ) : (
                                <i className="fas fa-check-circle color-green" />
                              )}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan={displayColumns.length + 1} className="td-transparent" />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="white-space-16" />

        {/* Financial Summary */}
        <div className="input-form-content row full align-center">
          <div className="column label-duo-small space">
            <p className="text-center">
              <b>SubTotal:</b>
            </p>
          </div>
          <div className="column label-duo-small space">
            <p className="text-center">
              <b>Impuestos:</b>
            </p>
          </div>
          <div className="column label-duo-small space">
            <p className="text-center">
              <b>Retenciones:</b>
            </p>
          </div>
          <div className="column label-duo-small space">
            <p className="text-center">
              <b>Total:</b>
            </p>
          </div>
        </div>
        <div className="input-form-content row full align-center">
          <div className="column full space">
            <input type="text" className="input input-modals text-right" value={formatCurrency(subtotal)} disabled />
          </div>
          <div className="column full space">
            <input
              type="text"
              className="input input-modals text-right"
              value={formatCurrency(impuestosTotal)}
              disabled
            />
          </div>
          <div className="column full space">
            <input
              type="text"
              className="input input-modals text-right"
              value={formatCurrency(retencionesTotal)}
              disabled
            />
          </div>
          <div className="column full space">
            <input type="text" className="input input-modals text-right" value={formatCurrency(total)} disabled />
          </div>
        </div>

        <div className="white-space-16" />

        {/* Validation errors / warnings */}
        {(validationSummary.errors.length > 0 || validationSummary.warnings.length > 0) && (
          <div style={{ marginBottom: 16, width: "100%" }}>
            {validationSummary.errors.length > 0 && (
              <div
                style={{ border: "1px solid #d14343", background: "#fff5f5", borderRadius: 6, padding: "12px 16px" }}
              >
                <p style={{ fontWeight: 600, margin: "0 0 8px 0" }}>
                  Corrige estos datos antes de importar ({validationSummary.errors.length})
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, color: "#7a0000" }}>
                  {errorPreview.map((err, idx) => (
                    <li key={`err-${idx}`}>{err}</li>
                  ))}
                </ul>
                {extraErrors > 0 && <p style={{ margin: "8px 0 0 0", color: "#7a0000" }}>y {extraErrors} más...</p>}
              </div>
            )}
            {validationSummary.warnings.length > 0 && (
              <div
                style={{
                  border: "1px solid #c77b00",
                  background: "#fff9ee",
                  borderRadius: 6,
                  padding: "12px 16px",
                  marginTop: 8,
                }}
              >
                <p style={{ fontWeight: 600, margin: "0 0 8px 0" }}>
                  Advertencias ({validationSummary.warnings.length})
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, color: "#6d4200" }}>
                  {warningPreview.map((w, idx) => (
                    <li key={`warn-${idx}`}>{w}</li>
                  ))}
                </ul>
                {extraWarnings > 0 && <p style={{ margin: "8px 0 0 0", color: "#6d4200" }}>y {extraWarnings} más...</p>}
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
});

export default Importar;
